import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from "react";
import { api } from "../services/api";
import toast from "react-hot-toast";
import { useAuth } from "./AuthContext";

export interface Category {
  id: string;
  name: string;
  name_th: string;
  name_en: string;
  name_es: string;
  name_ja: string;
  name_ko: string;
  name_id: string;
  name_ar: string;
  description: string;
  description_th: string;
  description_en: string;
  description_es: string;
  description_ja: string;
  description_ko: string;
  description_id: string;
  description_ar: string;
  prompt: string;
  freeUserPrompt: string;
  icon: string;
  color: string;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

interface CategoryContextType {
  categories: Category[];
  selectedCategory: string | null;
  isLoading: boolean;
  hasFetched: boolean;
  addCategory: (
    category: Omit<Category, "id" | "createdAt" | "updatedAt">,
  ) => Promise<void>;
  updateCategory: (id: string, updates: Partial<Category>) => Promise<void>;
  deleteCategory: (id: string) => Promise<void>;
  setSelectedCategory: (categoryId: string | null) => void;
  getCategoryById: (id: string) => Category | undefined;
  fetchCategories: () => Promise<void>;
}

const CategoryContext = createContext<CategoryContextType | undefined>(
  undefined,
);

export const useCategories = () => {
  const context = useContext(CategoryContext);
  if (!context) {
    throw new Error("useCategories must be used within a CategoryProvider");
  }
  return context;
};

interface CategoryProviderProps {
  children: ReactNode;
}

export const CategoryProvider: React.FC<CategoryProviderProps> = ({
  children,
}) => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);

  const { user } = useAuth();

  const addCategory = async (
    categoryData: Omit<Category, "id" | "createdAt" | "updatedAt">,
  ) => {
    try {
      const response = await api.post("/backend/category/add", {
        name: categoryData.name,
        name_en: categoryData.name_en,
        name_th: categoryData.name_th,
        name_es: categoryData.name_es,
        name_ja: categoryData.name_ja,
        name_ko: categoryData.name_ko,
        name_id: categoryData.name_id,
        name_ar: categoryData.name_ar,
        description: categoryData.description,
        description_en: categoryData.description_en,
        description_th: categoryData.description_th,
        description_es: categoryData.description_es,
        description_ja: categoryData.description_ja,
        description_ko: categoryData.description_ko,
        description_id: categoryData.description_id,
        description_ar: categoryData.description_ar,
        prompt: categoryData.prompt,
        freeUserPrompt: categoryData.freeUserPrompt,
        icon: categoryData.icon,
        color: categoryData.color,
        isActive: categoryData.isActive,
      });

      if (response.data.success) {
        const newCategory: Category = {
          ...categoryData,
          id: response.data.data._id,
          createdAt: new Date(),
          updatedAt: new Date(),
        };
        setCategories((prev) => [...prev, newCategory]);
      }
    } catch (error) {
      console.error("Failed to add category:", error);
      toast.error("เพิ่มหมวดหมู่ไม่สำเร็จ");
      throw error;
    }
  };

  const updateCategory = async (id: string, updates: Partial<Category>) => {
    try {
      console.log("updates log ->>", updates);
      await api.put(`/backend/category/${id}/update`, updates);
      setCategories((prev) =>
        prev.map((category) =>
          category.id === id
            ? { ...category, ...updates, updatedAt: new Date() }
            : category,
        ),
      );
    } catch (error) {
      console.error("Failed to update category:", error);
      toast.error("อัปเดตหมวดหมู่ไม่สำเร็จ");
      throw error;
    }
  };

  const fetchCategories = async (showToast = false) => {
    if (isLoading || !user) {
      return;
    }

    try {
      setIsLoading(true);
      const response = await api.get("/backend/categories");
      if (response.data.success) {
        const backendCategories = response.data.data.map((cat: any) => ({
          id: cat._id,
          name: cat.name,
          name_en: cat.name_en || cat.name,
          name_th: cat.name_th || "",
          name_es: cat.name_es || "",
          name_ja: cat.name_ja || "",
          name_ko: cat.name_ko || "",
          name_id: cat.name_id || "",
          name_ar: cat.name_ar || "",
          description: cat.description || "",
          description_en: cat.description_en || cat.description || "",
          description_th: cat.description_th || "",
          description_es: cat.description_es || "",
          description_ja: cat.description_ja || "",
          description_ko: cat.description_ko || "",
          description_id: cat.description_id || "",
          description_ar: cat.description_ar || "",
          prompt: cat.prompt || "",
          freeUserPrompt: cat.freeUserPrompt || "",
          icon: cat.icon || "💬",
          color: cat.color || "#3b82f6",
          isActive: cat.isActive,
          createdAt: new Date(cat.createdAt),
          updatedAt: new Date(cat.updatedAt),
        }));
        setCategories(backendCategories);
        setHasFetched(true);

        if (showToast && backendCategories.length > 0) {
          toast.success(`Loaded ${backendCategories.length} categories`);
        }
      }
    } catch (error) {
      console.error("Failed to fetch categories:", error);
      if (user && !hasFetched) {
        toast.error("โหลดหมวดหมู่ไม่สำเร็จ");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const deleteCategory = async (id: string) => {
    try {
      await api.delete(`/backend/category/${id}/delete`);
      setCategories((prev) => prev.filter((category) => category.id !== id));
    } catch (error) {
      console.error("Failed to delete category:", error);
      toast.error("ลบหมวดหมู่ไม่สำเร็จ");
      throw error;
    }
  };

  const getCategoryById = (id: string) => {
    return categories.find((category) => category.id === id);
  };

  // Initialize categories
  useEffect(() => {
    // Clear categories when user logs out
    if (!user) {
      setCategories([]);
      setHasFetched(false);
      return;
    }

    // Fetch categories when user logs in or changes
    if (user && !hasFetched) {
      fetchCategories(false);
    }
  }, [user]);

  useEffect(() => {
    if (user && !hasFetched && !isLoading) {
      fetchCategories(false);
    }
  }, [user, hasFetched, isLoading]);

  return (
    <CategoryContext.Provider
      value={{
        categories,
        selectedCategory,
        isLoading,
        hasFetched,
        addCategory,
        updateCategory,
        deleteCategory,
        setSelectedCategory,
        getCategoryById,
        fetchCategories,
      }}
    >
      {children}
    </CategoryContext.Provider>
  );
};
